import { useTranslation } from 'react-i18next';

import { usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import type { InventoryItem } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription, AlertTitle } from '@nucleus-commerce/ui/components/alert';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@nucleus-commerce/ui/components/card';

import { useSetTracking } from '../hooks/use-inventory-mutations';
import { isLowStock } from '../schemas/inventory-rules';

export interface ItemSummaryCardProps {
  storeId: string;
  storeName: string;
  item: InventoryItem;
}

/** Totals over the store's locations, and the way in for a variant that does not track stock. */
export function ItemSummaryCard({ storeId, storeName, item }: ItemSummaryCardProps) {
  const { t } = useTranslation('inventory');
  const { can } = usePermissions();
  const tracking = useSetTracking(storeId, item.productId, item.variantId);
  const lowAt = item.levels.filter(isLowStock).length;
  const total = (label: string, value: number, strong = false) => (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-muted-foreground text-sm">{label}</dt>
      <dd className={strong ? 'text-lg font-semibold tabular-nums' : 'tabular-nums'}>{value}</dd>
    </div>
  );

  if (!item.trackInventory) {
    return (
      <Alert data-tour="inventory.item.not-tracked">
        <AlertTitle>{t('item.notTracked.title')}</AlertTitle>
        <AlertDescription className="flex flex-col items-start gap-3">
          <span>{t('item.notTracked.description')}</span>
          {can('products.write') ? (
            <Button size="sm" disabled={tracking.isPending} onClick={() => tracking.mutate(true)}>
              {t('item.notTracked.action')}
            </Button>
          ) : (
            <span className="text-muted-foreground text-xs">
              {t('item.notTracked.noPermission')}
            </span>
          )}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Card className="gap-0 py-0" data-tour="inventory.item.summary">
      <CardHeader className="px-6 py-4">
        <CardTitle className="text-sm">{t('columns.available')}</CardTitle>
        <CardDescription>{t('item.totals.hint', { store: storeName })}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4 px-6 pb-5">
        <dl className="flex flex-col gap-2">
          {total(t('item.totals.onHand'), item.onHand)}
          {total(t('item.totals.reserved'), item.reserved)}
          {total(t('item.totals.available'), item.available, true)}
        </dl>
        <div className="flex flex-wrap gap-1.5">
          <Badge variant="secondary">{t('item.tracked')}</Badge>
          {item.allowBackorder ? <Badge variant="outline">{t('item.backorder')}</Badge> : null}
          {lowAt > 0 ? (
            <Badge
              variant="outline"
              className="bg-warning/20 text-warning-foreground dark:bg-warning/25 border-transparent"
            >
              {t('item.lowAt', { count: lowAt })}
            </Badge>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}
